import VoucherDetailInertiaController from '@/actions/App/Http/Controllers/VoucherDetailInertiaController';
import { VoucherChip } from '@/components/common/voucher-chip';
import { useVoucherCopy } from '@/hooks/use-voucher-copy';
import { cn } from '@/lib/utils';
import { type BookingRate } from '@/types/search';
import { type AvailableVoucher } from '@/types/voucher';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';

const formatVND = (amount: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

const DEFAULT_PILLS = ['Không hoàn huỷ', 'Hiệu lực vào ngày đã chọn', 'Vào cổng bằng voucher', 'Xác nhận tức thời'];

interface RateCardProps {
  rate: BookingRate;
  index: number;
  searchParams: {
    site: string;
    date: string;
  };
  /** Vouchers scoped to this exact rate. No site-level or global fallback here. */
  vouchers?: AvailableVoucher[];
  onAddToCart: (rate: BookingRate) => void;
  onViewDetail?: (rate: BookingRate) => void;
  className?: string;
}

export function RateCard({ rate, vouchers, onAddToCart, onViewDetail, className }: RateCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const { copyState, copy } = useVoucherCopy();

  const hasDiscount = rate.RateDiscountPercent > 0;
  const prices = rate.Services.map((s) => s.Price).filter((p) => p > 0);
  const basePrices = rate.Services.map((s) => s.BasePrice).filter((p) => p > 0);
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const basePrice = basePrices.length ? Math.min(...basePrices) : 0;

  return (
    <div className={cn('rounded-xl border border-(--pill-border) bg-white p-5', className)}>
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <span className="text-base font-semibold text-gray-900">{rate.DisplayName || rate.Name}</span>
        <button
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Thu gọn' : 'Mở rộng'}
          className="flex-shrink-0 rounded-md p-0.5 text-gray-400 transition-colors hover:text-gray-600"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')} />
        </button>
      </div>

      {/* Feature pills */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {DEFAULT_PILLS.map((pill) => (
          <span key={pill} className="rounded-full border border-(--pill-border) bg-(--pill-bg) px-2.5 py-1 text-xs text-gray-600">
            {pill}
          </span>
        ))}
      </div>

      {/* Discount chip */}
      {hasDiscount && (
        <div className="mb-3">
          <span className="bg-primary inline-flex rounded-sm px-2 py-0.5 text-xs font-semibold text-white">Giảm {rate.RateDiscountPercent}%</span>
        </div>
      )}

      {/* Vouchers that apply to this rate specifically */}
      {vouchers && vouchers.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {vouchers.map((voucher) => (
            <VoucherChip
              key={voucher.id}
              voucher={voucher}
              detailHref={VoucherDetailInertiaController.show(voucher.code).url}
              copyState={copiedCode === voucher.code ? copyState : 'idle'}
              onCopy={(code) => {
                setCopiedCode(code);
                copy(code);
              }}
            />
          ))}
        </div>
      )}

      <div className="mb-3 h-px bg-(--pill-border)" />

      {/* Bottom row */}
      <div className="flex items-end justify-between gap-2">
        <Button variant="ghost" onClick={() => onViewDetail?.(rate)} className="text-xs text-blue-600 hover:text-blue-600 hover:underline">
          Thông tin gói dịch vụ ›
        </Button>

        <div className="flex items-center gap-3">
          <div className="text-right">
            {hasDiscount && basePrice > 0 && <span className="text-muted-foreground block text-xs line-through">{formatVND(basePrice)}</span>}
            {minPrice > 0 && <span className="text-base font-bold text-gray-900">{formatVND(minPrice)}</span>}
          </div>
          <Button onClick={() => onAddToCart(rate)}>Chọn</Button>
        </div>
      </div>

      {/* Expanded services list */}
      {expanded && rate.Services.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-(--pill-border) pt-4">
          {rate.Services.map((service) => (
            <div key={service.Code} className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{service.DisplayName || service.Name || service.ShortName}</span>
              <span className="font-medium text-gray-900">{service.Price > 0 ? formatVND(service.Price) : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
